import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, fromEvent } from 'rxjs';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import { AGENT_SESSION_FRESHNESS_MS, ThreadTab } from '../agent.types';
import { writeLastOpenThreadId, clearLastOpenThreadIdIfMatches } from '../agent.fullpage.memory';
import { BackgroundStreamManager } from './background.stream.manager';
import type { EnrichedThreadTab } from '../agent.types';

const MAX_TABS = 10;
// Tabs + activeTabId persist in localStorage so they're shared across browser tabs
// (same conversations everywhere). DisplayMode persists in sessionStorage so it's
// per-tab — opening a new tab doesn't inherit pseudo from the originating tab,
// and refreshing a tab keeps its pseudo/popup state isolated from other tabs.
// The persisted tabs carry a `ts` of the last agent interaction. On load, if that
// is older than the shared session-freshness window, the whole bar is dropped —
// returning after a long gap starts clean instead of reopening stale (uncached,
// expensive) threads. Uses the same window duration as the fullpage last-chat,
// though each surface tracks its own last-interaction time (not a single clock).
const STORAGE_KEY = 'agent-tabs';
const DISPLAY_MODE_KEY = 'agent-display-mode';

/** Reserved tab IDs for non-thread tabs (new chat, history dropdown). */
export const RESERVED_TAB_ID = {
  NEW_CHAT: '__new__',
  HISTORY: '__history__',
} as const;

/** Draft key for a not-yet-saved new chat (no threadId yet). Distinct from the tab IDs above. */
export const NEW_CHAT_DRAFT_KEY = '__new_chat__';

export type DisplayMode = 'popup' | 'pseudo';

// How the popup panel should disappear the next time it hides: 'minimize' animates
// out (mirrors the open animation), 'close' vanishes instantly. Set by the relevant
// action before $activeTabId is cleared.
export type PanelExitMode = 'minimize' | 'close';

interface PersistedTabsState {
  tabs: ThreadTab[];
  activeTabId: string | null;
  ts: number;
}

function loadTabsState(): PersistedTabsState {

  const empty: PersistedTabsState = { tabs: [], activeTabId: null, ts: Date.now() };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<PersistedTabsState>;
    // Stale session: drop the whole bar rather than reopening day-old threads.
    if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > AGENT_SESSION_FRESHNESS_MS) return empty;
    return {
      tabs: Array.isArray(parsed.tabs) ? parsed.tabs : [],
      activeTabId: parsed.activeTabId ?? null,
      ts: typeof parsed.ts === 'number' ? parsed.ts : Date.now(),
    };
  } catch {
    return empty;
  }
}

function loadDisplayMode(): DisplayMode {

  const raw = sessionStorage.getItem(DISPLAY_MODE_KEY);
  return raw === 'pseudo' ? 'pseudo' : 'popup';
}

function persistToStorage(storage: Storage, key: string, value: string): void {

  try {
    storage.setItem(key, value);
  } catch {
    // Storage unavailable (quota / private mode) — skip persistence.
  }
}

@Injectable({ providedIn: 'root' })
export class AgentPanelService {

  private readonly savedTabsState = loadTabsState();
  private readonly backgroundManager = inject(BackgroundStreamManager);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private tabsInitialized = false;
  private displayModeInitialized = false;
  private lastUrl: string | null = null;
  private lastNonAgentUrl: string | null = null;
  // `undefined` means the fullscreen surface is not mounted; `null` means it is mounted on New Chat
  // or Automations; a string is the conversation currently visible there. Keeping this one location
  // signal here lets completion suppression cover popup and fullscreen without duplicating chat state.
  private readonly $fullpageActiveThreadId = signal<string | null | undefined>(undefined);

  public readonly $openTabs = signal<ThreadTab[]>(this.savedTabsState.tabs);
  public readonly $activeTabId = signal<string | null>(this.savedTabsState.activeTabId);
  public readonly $historyOpen = signal<boolean>(false);
  public readonly $displayMode = signal<DisplayMode>(loadDisplayMode());

  // Per-conversation composer drafts, keyed by threadId (or NEW_CHAT_DRAFT_KEY for a not-yet-saved
  // new chat). Root-shared so popup + fullscreen on the SAME thread share one draft and it survives
  // the prompt-input being torn down by a card overlay — but a different thread (or a fresh new chat)
  // gets its own draft and never inherits another conversation's text.
  private readonly $composerDrafts = signal<Record<string, string>>({});

  public composerDraftFor(key: string): string {

    return this.$composerDrafts()[key] ?? '';
  }

  public setComposerDraft(key: string, value: string): void {

    this.$composerDrafts.update(drafts => ({ ...drafts, [key]: value }));
  }

  public clearComposerDraft(key: string): void {

    this.$composerDrafts.update(drafts => { const next = { ...drafts }; delete next[key]; return next; });
  }

  // Whether the panel's next disappearance should animate (minimize) or be instant
  // (close/cross). The shell reads this to pick the exit transition.
  public readonly $panelExitMode = signal<PanelExitMode>('minimize');

  /** Whether the active tab is the new-chat tab */
  public readonly $newChatOpen = computed(() => this.$activeTabId() === RESERVED_TAB_ID.NEW_CHAT);

  /** The active real thread ID (excludes reserved tabs) */
  public readonly $activeThreadId = computed(() => {

    const id = this.$activeTabId();
    return id && id !== RESERVED_TAB_ID.NEW_CHAT ? id : null;
  });

  public readonly $panelOpen = computed(() => this.$activeThreadId() !== null || this.$newChatOpen());

  // True once the popup panel is fully positioned and revealed (visibility:visible).
  // The shell sets this after measuring the popup anchor + double-rAF; consumers
  // (e.g. prompt input focus) react to this instead of polling for visibility.
  // Stays true in fullpage mode where panel-hidden does not apply.
  public readonly $panelVisible = signal<boolean>(false);

  public readonly $enrichedTabs = computed<EnrichedThreadTab[]>(() => {

    const tabs = this.$openTabs();
    const states = this.backgroundManager.$threadStates();
    return tabs.map(tab => ({
      ...tab,
      streaming: states.get(tab.threadId)?.streaming ?? false,
      unread: states.get(tab.threadId)?.unread ?? false,
    }));
  });

  private readonly _persistTabs = effect(() => {

    const id = this.$activeTabId();
    const tabs = this.$openTabs();

    if (!this.tabsInitialized) {
      this.tabsInitialized = true;
      return;
    }

    // Stamp the interaction time so the session-freshness window measures idle
    // from the last tab/panel change (open, activate, collect, close).
    const state: PersistedTabsState = { tabs, activeTabId: id ?? null, ts: Date.now() };
    persistToStorage(localStorage, STORAGE_KEY, JSON.stringify(state));
  });

  private readonly _persistDisplayMode = effect(() => {

    const mode = this.$displayMode();

    if (!this.displayModeInitialized) {
      this.displayModeInitialized = true;
      return;
    }

    persistToStorage(sessionStorage, DISPLAY_MODE_KEY, mode);
  });

  private readonly _notifyCompletion = effect(() => {

    const completed = this.backgroundManager.$lastCompleted();
    if (!completed) return;
    untracked(() => this.notifyThreadCompleted(completed.threadId));
  });

  constructor() {

    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(filter(e => e.key === 'Escape'))
      .subscribe(e => {
        if (!this.$panelOpen()) return;
        if (e.defaultPrevented) return;
        this.minimize();
      });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(event => {
        const url = event.urlAfterRedirects;
        const previous = this.lastUrl;
        this.lastUrl = url;

        // Remember the last page that wasn't the fullpage agent surface. Minimizing /agent
        // returns here in a single navigation — location.back() is unreliable because thread
        // switches stack /agent?threadId history entries that back() bounces through one at a
        // time, and the shell tears the panel down on each re-landing on /agent.
        if (!url.startsWith('/agent')) this.lastNonAgentUrl = url;

        // Skip the initial NavigationEnd (bootstrap) so a persisted pseudo state survives reload.
        if (previous === null) return;

        // Path-only check — ignore in-page query/fragment updates.
        const previousPath = previous.split('?')[0].split('#')[0];
        const currentPath = url.split('?')[0].split('#')[0];
        if (previousPath === currentPath) return;

        this.collapsePseudoIfOpen();
      });
  }

  private collapsePseudoIfOpen(): void {

    if (this.$displayMode() === 'pseudo' && this.$panelOpen()) {
      this.setDisplayMode('popup');
    }
  }

  public openNewChat(): void {

    // Every open lands in the windowed (popup) form, regardless of any prior
    // expanded state this browser tab had persisted.
    this.$displayMode.set('popup');
    this.$activeTabId.set(RESERVED_TAB_ID.NEW_CHAT);
    this.$historyOpen.set(false);
    // New Chat is the active focus, so fullscreen should open to a blank chat too.
    writeLastOpenThreadId(null);
  }

  public toggleTab(tabId: string): void {

    if (this.$activeTabId() === tabId) {
      this.$activeTabId.set(null);
      return;
    }
    // Opening a tab always opens the windowed form (never the expanded surface).
    this.$displayMode.set('popup');
    this.$activeTabId.set(tabId);
    this.$historyOpen.set(false);
    // Keep fullscreen's last-open memory in step with the panel: focusing a real
    // thread makes /agent open to it; choosing New Chat resets it to a blank chat.
    this.syncFullpageMemory(tabId);
  }

  public addTab(threadId: string, title: string): void {

    this.collectTab(threadId, title);
    // Reopening a chat (from history, a toast, or the fullpage collect) opens windowed.
    this.$displayMode.set('popup');
    this.$activeTabId.set(threadId);
    this.$historyOpen.set(false);
    writeLastOpenThreadId(threadId);
  }

  // Add (or update the title of) a tab WITHOUT activating it or opening the panel.
  // Used to passively collect the last-viewed fullpage thread into the bottom bar
  // when leaving /agent, so it stays reachable without forcing the popup open.
  public collectTab(threadId: string, title: string): void {

    this.$openTabs.update(tabs => {
      const existing = tabs.find(t => t.threadId === threadId);
      if (existing) {
        return tabs.map(t => t.threadId === threadId ? { ...t, title } : t);
      }
      const next = [...tabs, { threadId, title }];
      if (next.length > MAX_TABS) next.shift();
      return next;
    });
  }

  // Title-only update — does not activate the tab or close history. The user
  // may have moved on by the time the generated title arrives, and silently
  // no-ops when the tab no longer exists (e.g. closed before generation).
  public updateTabTitle(threadId: string, title: string): void {

    const tabs = this.$openTabs();
    if (!tabs.some(t => t.threadId === threadId)) return;
    this.$openTabs.set(tabs.map(t => t.threadId === threadId ? { ...t, title } : t));
  }

  public closeTab(threadId: string): void {

    this.$openTabs.update(tabs => tabs.filter(t => t.threadId !== threadId));
    // Closing a chat also dismisses it from the fullpage's last-open memory, so
    // opening /agent afterwards doesn't resurface a chat crossed off the bar.
    clearLastOpenThreadIdIfMatches(threadId);
    if (this.$activeTabId() === threadId) {
      this.$panelExitMode.set('close');
      this.$activeTabId.set(null);
    }
  }

  /** Close the entire panel — resets all state. */
  public closePanel(): void {

    this.$panelExitMode.set('close');
    this.$activeTabId.set(null);
    this.$historyOpen.set(false);
  }

  public minimize(): void {

    this.$panelExitMode.set('minimize');
    this.$activeTabId.set(null);
  }

  // The last non-/agent page visited, used to leave the fullpage agent surface on
  // minimize. Falls back to the app root (redirects to home) when the agent was the
  // first page loaded (deep link / refresh on /agent), so we never fail to exit.
  public get fullpageReturnUrl(): string {

    return this.lastNonAgentUrl ?? '/';
  }

  /** Publish the conversation currently visible on the legacy fullscreen surface. */
  public setFullpageActiveThread(threadId: string | null): void {

    this.$fullpageActiveThreadId.set(threadId);
  }

  /** Mark fullscreen as unmounted so future completion actions can open the popup in place. */
  public clearFullpageActiveThread(): void {

    this.$fullpageActiveThreadId.set(undefined);
  }

  // Mirrors the activated tab into fullscreen's last-open memory: a real thread becomes
  // the chat /agent restores, New Chat resets it to blank. The history tab is a dropdown
  // toggle, not a conversation, so it leaves the memory untouched.
  private syncFullpageMemory(tabId: string): void {

    if (tabId === RESERVED_TAB_ID.HISTORY) return;
    writeLastOpenThreadId(tabId === RESERVED_TAB_ID.NEW_CHAT ? null : tabId);
  }

  public reopenFromHistory(threadId: string, title: string): void {

    this.addTab(threadId, title);
  }

  public toggleHistory(): void {

    this.$historyOpen.update(v => !v);
  }

  public setDisplayMode(mode: DisplayMode): void {

    this.$displayMode.set(mode);
  }

  private notifyThreadCompleted(threadId: string): void {

    if (this.$activeThreadId() === threadId || this.$fullpageActiveThreadId() === threadId) return;
    const title = this.$openTabs().find(tab => tab.threadId === threadId)?.title?.trim();
    const detail = title ? `"${title}" is ready to view.` : 'Your chat is ready to view.';
    const viewAction = new Command({ label: 'View', action: () => this.openCompletedThread(threadId, title) });
    this.toastService.success('Workbuddy Agent finished', detail, [viewAction], { life: 6000, styleClass: 'agent-completion-toast' });
  }

  private openCompletedThread(threadId: string, title?: string): void {

    const collectedTab = this.$openTabs().find(tab => tab.threadId === threadId);
    if (!collectedTab) {
      void this.router.navigate(['/agent'], { queryParams: { threadId } });
      return;
    }

    // The shell intentionally hides popup chat while /agent is mounted. Leave fullscreen first so
    // the already-collected tab can appear in its normal windowed form. Activate it AFTER navigation:
    // fullscreen's destroy hook may collect its old chat into a full tab bar and evict the oldest tab,
    // so this ordering guarantees the requested completed chat is the final collected/active one.
    if (this.$fullpageActiveThreadId() !== undefined) {
      void this.router.navigateByUrl(this.fullpageReturnUrl)
        .then(() => this.addTab(threadId, collectedTab.title?.trim() || title || 'Chat'));
      return;
    }

    this.addTab(threadId, collectedTab.title?.trim() || title || 'Chat');
  }
}
